import React from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { BookOpen, Users, LightbulbIcon, Award, GraduationCap, FileText } from 'lucide-react';
import PageLayout from '../../layouts/PageLayout';
import { Section, SectionHeader } from '../../components/shared/Section';
import { Card, CardHeader, CardContent } from '../../components/shared/Card';
import { TagList } from '../../components/shared/TagSystem';
import { cn } from '../../lib/utils';
import { getThemeClass } from '../../lib/theme';
import { HeroRibbon } from '../Home/components/HeroSection';

interface Event {
  name: string;
  location: string;
  topics: string[];
}

interface ConferenceWorkshop {
  title: string;
  events: Event[];
}

interface InstitutionWorkshop {
  title: string;
  institution: string;
  contributions: string[];
  tools: string[];
}

type Workshop = ConferenceWorkshop | InstitutionWorkshop;

const isConferenceWorkshop = (workshop: Workshop): workshop is ConferenceWorkshop => {
  return 'events' in workshop;
};

const Research: React.FC = () => {
  usePageTitle('Research & Publications');

  // Featured publication data - VIVO (Primary external impact evidence)
  const featuredPublication = {
    title: "Analyzing and Visualizing VIVO Data",
    publication: "VIVO: A Semantic Portal for Scholarly Networking",
    publisher: "Springer International Publishing (2012)",
    isbn: "9783031794346",
    doi: "10.1007/978-3-031-79435-3_7",
    collaboration: "Distinguished Professor Katy Börner, Indiana University",
    funding: {
      source: "National Institutes of Health (NIH)",
      grant: "U24 RR029822",
      projectTitle: "VIVO: Enabling National Networking of Scientists",
      period: "2009-2012"
    },
    researchContribution: "Led visualization architecture design for NIH-funded national researcher networking platform, creating frameworks that enabled third-party integration with semantic data backends. Developed sparklines, egocentric, and temporal graph visualizations included in official VIVO release.",
    technicalInnovations: [
      "Designed modular visualization framework enabling institutional adoption",
      "Created caching architecture optimizing complex visualization performance",
      "Developed gracefully-degrading systems handling varied data quality",
      "Built client-server architecture supporting multi-institutional deployment"
    ],
    academicImpact: {
      adoption: [
        "Framework adopted by research institutions nationwide",
        "International implementations across university systems",
        "Continuing usage over a decade after initial development (2012-2025+)",
        "Integrated into official VIVO software releases"
      ],
      citations: [
        "Documented citations across Web of Science, Google Scholar, and ResearchGate",
        "Framework adopted by research institutions with 10+ year sustained usage",
        "Referenced in peer-reviewed journals and conference proceedings",
        "52+ publication views on Springer platform"
      ],
      recognition: [
        "Collaboration with Distinguished Professor Katy Börner",
        "Part of NSF-funded Cyberinfrastructure for Network Science Center",
        "Integration with Network Workbench (NWB) and Science of Science (Sci2) tools",
        "Contribution to digital humanities research methodologies"
      ]
    },
    knowledgeTranslation: [
      "Informed enterprise-scale CDN infrastructure serving 292,600+ sites",
      "Contributed to patented data sharing systems (US Patent 10,908,826)",
      "Led to performance optimization reducing bandwidth usage by 80%",
      "Influenced distributed systems architecture across Fortune 500 implementations"
    ],
    technologies: ["Visualization Framework", "Semantic Data", "Network Analysis", "Java", "CIShell"]
  };

  // Co-authored research publications
  const coAuthoredPublications = [
    {
      title: "Network and Topical Analysis for the Humanities using NWB and Sci2",
      authors: "Weingart, S.B., Börner, K., Duhon, R.J., Linnemeier, M., Phillips, P., Biberstine, J., Tank, C., Kong, C.H.",
      conference: "Digital Humanities (DH) 2011",
      pages: "22-26",
      description: "Extended network analysis techniques to humanities research, bridging technological gaps between scientific and humanities research methodologies. Work contributed to interdisciplinary research infrastructure development."
    },
    {
      title: "Science of Science (Sci2) Tool Development",
      contribution: "Core algorithm development and plugin architecture",
      tools: ["Network Workbench (NWB)", "Science of Science (Sci2)", "Epidemics Cyberinfrastructure (EpiC)"],
      collaboration: "Cyberinfrastructure for Network Science Center, Indiana University",
      description: "Contributed to foundational research tools used by multiple academic institutions for network analysis, scientific collaboration visualization, and cross-disciplinary data analysis.",
      sustainedImpact: "Tools and methodologies continue to be cited and utilized over a decade after initial development, demonstrating lasting contribution to research infrastructure."
    }
  ];

  // Professional recognition and credentials
  const professionalRecognition = {
    memberships: [
      {
        title: "IEEE Senior Member",
        organization: "Institute of Electrical and Electronics Engineers",
        description: "Advanced grade membership requiring demonstrated professional accomplishment"
      },
      {
        title: "IETE Fellow",
        organization: "Institution of Electronics and Telecommunication Engineers",
        description: "Fellow designation recognizing distinguished contributions to the field"
      },
      {
        title: "Sigma Xi, The Scientific Research Honor Society",
        organization: "Full Member (2026)",
        description:
          "Selective honor society recognizing demonstrated research achievement across scientific disciplines. Full Membership requires nomination and election by existing members."
      }
    ],
    activities: [
      "Technical Program Committee (TPC) Reviewer, IEEE SoutheastCon 2026 - Completed 5 double-blind peer reviews across Computing Systems and AI/Predictive Modeling tracks (January 2026)",
      "Reviewer, IEEE NFV-SDN 2026 - Completed peer review for the CDN track",
      "Reviewer, ICCISCS 2026 - Completed peer review",
      "Technical evaluation contributor for academic and industry publications"
    ]
  };

  // Technical workshops data - expanded with specific conference details
  const workshops: Workshop[] = [
    {
      title: "VIVO Conference Series",
      events: [
        {
          name: "Second Annual VIVO Conference (2011)",
          location: "Washington, DC",
          topics: [
            "Tutorial: \"Visualization in VIVO: A case study in how VIVO data and technology can be used\"",
            "Presenters: Katy Börner, Chintan Tank, Chin Hua Kong",
            "Map of Science implementation and temporal graph visualization"
          ]
        },
        {
          name: "First Annual VIVO Conference (2010)",
          location: "New York City",
          topics: [
            "Workshop: \"VIVO Data Analysis and Visualization: How to Program, Extend and Utilize\"",
            "Presenters: Micah Linnemeier, Chintan Tank, Katy Börner",
            "Social network visualizations and data architecture"
          ]
        },
        {
          name: "Invited talk, Indiana University CNS Research Showcase",
          location: "July 6, 2026 · Indiana University Bloomington",
          topics: [
            "Hosted by the Cyberinfrastructure for Network Science (CNS) Center, Luddy School of Informatics, Computing, and Engineering",
            "Lab director: Dr. Katy Börner",
            "Topic: LLM-driven knowledge graphs over heterogeneous codebases for AI agent reasoning, including semantic indexing of software artifact networks",
            "Returned to the same lab and collaborator behind the 2012 VIVO chapter"
          ]
        }
      ]
    },
    {
      title: "Academic Institution Workshops",
      institution: "Cyberinfrastructure for Network Sciences Center, Indiana University",
      contributions: [
        "Developed core plugins for CIShell-based tools",
        "Implemented foundational algorithms for network analysis",
        "Led architectural development for research platforms",
        "Created visualization frameworks for cross-disciplinary research"
      ],
      tools: ["Network Workbench (NWB)", "Science of Science (Sci2)", "Epidemics Cyberinfrastructure (EpiC)"]
    }
  ];

  // Patents and innovations - expanded with impact metrics
  const patents = [
    {
      title: "System to Dynamically Share Data by Business Relationship Type",
      id: "US Patent 10,908,826",
      issued: "February 2, 2021",
      description: "Innovative system for dynamic data sharing based on business relationships, contributing to enterprise platform capabilities.",
      link: "https://patents.google.com/patent/US10908826B2/en?oq=Patent+US+10908826",
      impact: [
        "20 patent citations from subsequent innovations",
        "6 non-patent citations in academic literature",
        "Currently implemented in Salesforce enterprise platform",
        "Affects thousands of enterprise customers globally",
        "Enables complex business relationship-based data sharing"
      ]
    }
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroRibbon
        title="Research & Publications"
        subtitle="Research and professional writing across AI systems, edge computing, semantic data, and software architecture, plus patents, technical presentations, and peer-review service."
            animate={false}
      />

      {/* Publications - VIVO book chapter and recent IEEE/Springer work */}
      <Section background="light" spacing="sm" animate={false}>
        <SectionHeader
          title="Publications"
          subtitle="Peer-reviewed IEEE and Springer work, professional articles, and NIH-funded scholarly networking infrastructure"
          align="left"
        />
        
        <Card
          variant="default"
          size="lg"
          className={cn(
            'transition-all duration-300',
            getThemeClass('colors.border.hover', 'borderHover')
          )}
        >
          <CardHeader
            icon={BookOpen}
            title={featuredPublication.publication}
            subtitle={featuredPublication.title}
            iconClassName={cn(
              'w-8 h-8',
              getThemeClass('colors.primary.main')
            )}
            link="https://link.springer.com/chapter/10.1007/978-3-031-79435-3_7"
          />
          <CardContent>
            <div className="space-y-6">
              {/* Publication Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className={getThemeClass('colors.text.secondary')}>
                    {featuredPublication.publisher}
                  </p>
                  <p className={getThemeClass('colors.text.secondary')}>
                    ISBN: {featuredPublication.isbn}
                  </p>
                  <p className={getThemeClass('colors.text.secondary')}>
                    DOI: {featuredPublication.doi}
                  </p>
                </div>
                <div>
                  <p className={cn('font-medium', getThemeClass('colors.text.primary'))}>
                    Collaboration
                  </p>
                  <p className={getThemeClass('colors.text.secondary')}>
                    {featuredPublication.collaboration}
                  </p>
                </div>
              </div>

              {/* NIH Funding */}
              <div className={cn(
                'rounded-lg p-4',
                'bg-gradient-to-r from-sky-50 to-indigo-50',
                'border border-sky-200'
              )}>
                <p className={cn('font-semibold mb-2', getThemeClass('colors.text.primary'))}>
                  Research Funding
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">{featuredPublication.funding.source}</span> Grant {featuredPublication.funding.grant}
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Project: "{featuredPublication.funding.projectTitle}"
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Period: {featuredPublication.funding.period}
                </p>
              </div>

              {/* Research Contribution */}
              <div>
                <h4 className={cn('text-lg font-semibold mb-3', getThemeClass('colors.text.primary'))}>
                  Research Contribution
                </h4>
                <p className={cn('mb-4', getThemeClass('colors.text.secondary'))}>
                  {featuredPublication.researchContribution}
                </p>
                <p className={cn('font-medium mb-2', getThemeClass('colors.text.primary'))}>
                  Core Technical Innovation:
                </p>
                <ul className="space-y-2">
                  {featuredPublication.technicalInnovations.map((innovation, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className={cn(
                        'w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0',
                        'bg-sky-500'
                      )}/>
                      <span className={getThemeClass('colors.text.secondary')}>
                        {innovation}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Academic Impact & Framework Adoption */}
              <div>
                <h4 className={cn('text-lg font-semibold mb-4', getThemeClass('colors.text.primary'))}>
                  Academic Impact & Framework Adoption
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Multi-Institutional Adoption */}
                  <div className={cn(
                    'rounded-lg p-4 border',
                    getThemeClass('colors.background.surface'),
                    'border-slate-200'
                  )}>
                    <p className={cn('font-medium mb-3', getThemeClass('colors.text.primary'))}>
                      Multi-Institutional Adoption
                    </p>
                    <ul className="space-y-2">
                      {featuredPublication.academicImpact.adoption.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 bg-emerald-500"/>
                          <span className={cn('text-sm', getThemeClass('colors.text.secondary'))}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Academic Citation Impact */}
                  <div className={cn(
                    'rounded-lg p-4 border',
                    getThemeClass('colors.background.surface'),
                    'border-slate-200'
                  )}>
                    <p className={cn('font-medium mb-3', getThemeClass('colors.text.primary'))}>
                      Academic Citation Impact
                    </p>
                    <ul className="space-y-2">
                      {featuredPublication.academicImpact.citations.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 bg-sky-500"/>
                          <span className={cn('text-sm', getThemeClass('colors.text.secondary'))}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Academic Recognition */}
                  <div className={cn(
                    'rounded-lg p-4 border',
                    getThemeClass('colors.background.surface'),
                    'border-slate-200'
                  )}>
                    <p className={cn('font-medium mb-3', getThemeClass('colors.text.primary'))}>
                      Academic Recognition
                    </p>
                    <ul className="space-y-2">
                      {featuredPublication.academicImpact.recognition.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 bg-indigo-500"/>
                          <span className={cn('text-sm', getThemeClass('colors.text.secondary'))}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Knowledge Translation to Industry */}
              <div className={cn(
                'rounded-lg p-4',
                'bg-gradient-to-r from-emerald-50 to-sky-50',
                'border border-emerald-200'
              )}>
                <h4 className={cn('font-semibold mb-3', getThemeClass('colors.text.primary'))}>
                  Knowledge Translation to Industry
                </h4>
                <p className={cn('mb-3 text-sm', getThemeClass('colors.text.secondary'))}>
                  The visualization expertise and architectural patterns developed through academic research directly influenced later industry innovations:
                </p>
                <ul className="space-y-2">
                  {featuredPublication.knowledgeTranslation.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 bg-emerald-500"/>
                      <span className={cn('text-sm', getThemeClass('colors.text.secondary'))}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technologies */}
              <TagList
                tags={featuredPublication.technologies.map(tech => ({
                  label: tech,
                  variant: 'primary',
                  size: 'md'
                }))}
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 mt-6">
          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={FileText}
              title="Modular Edge Computing at Multi-Tenant SaaS Scale on Cloudflare Workers"
              subtitle="Professional / Trade Publication Article"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
              link="https://www.infoq.com/articles/modular-edge-computing/"
            />
            <CardContent>
              <div className="space-y-3">
                <p className={getThemeClass('colors.text.secondary')}>
                  Published by InfoQ. This is a professional article, not a scholarly or peer-reviewed publication.
                </p>
                <a
                  href="https://www.infoq.com/articles/modular-edge-computing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn('inline-block font-medium underline', getThemeClass('colors.primary.main'))}
                >
                  Read the article on InfoQ
                </a>
              </div>
            </CardContent>
          </Card>

          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={FileText}
              title="AI-Integrated DevSecOps for Scalable, Secure, and Modern Cloud Architectures"
              subtitle="IEEE Conference Paper (2026) - Published"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
              link="https://ieeexplore.ieee.org/document/11416586"
            />
            <CardContent>
              <div className="space-y-3">
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Authors:</span> Mahimalur, R.K., Kolla, V.G., Tank, C.D., Meesala, S.T.
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Published in:</span> 2026 International Conference on Electronics and Renewable Systems (ICEARS 2026)
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Publisher:</span> IEEE
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Date Added to IEEE Xplore:</span> March 9, 2026
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">DOI:</span>{' '}
                  <a
                    href="https://ieeexplore.ieee.org/document/11416586"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn('underline', getThemeClass('colors.primary.main'))}
                  >
                    10.1109/ICEARS67481.2026.11416586
                  </a>
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Status:</span> ✅ Live on IEEE Xplore
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Proposes an AI-Integrated DevSecOps framework embedding automated security intelligence into CI/CD pipelines. Combines fine-tuned LLMs for secure code review, ML-based risk prioritization, and anomaly detection for runtime telemetry. Results: F1-score 97.5%, mean time to detect 4.5 min.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={FileText}
              title="Agentic CDNs: A Multi-Agent Architecture for Edge-Native AI Inference and Control"
              subtitle="IEEE Conference Paper (2026) - Published"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
              link="https://ieeexplore.ieee.org/document/11608667"
            />
            <CardContent>
              <div className="space-y-3">
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Authors:</span> Kolla, V.G., Tank, C.D., Giavelli, L.
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Published in:</span> 2026 IEEE International Conference on AI and Data Analytics (ICAD), Boston, MA
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Conference date:</span> June 11-12, 2026
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Date Added to IEEE Xplore:</span> July 22, 2026
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">DOI:</span>{' '}
                  <a
                    href="https://doi.org/10.1109/ICAD69378.2026.11608667"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn('underline', getThemeClass('colors.primary.main'))}
                  >
                    10.1109/ICAD69378.2026.11608667
                  </a>
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Status:</span> Published on IEEE Xplore
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Presents a hierarchical multi-agent framework for converting CDNs into distributed AI inference systems with edge-native control. Results include 41.2% decrease in inference latency, 34.8% improvement in cache hit rate, and 62% reduction in backbone bandwidth usage.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={FileText}
              title="Collaborative Edge-Cloud Inference for Real-Time Multimedia Delivery over Next-Gen CDNs"
              subtitle="ICCCNet 2026 Conference Paper - Presented"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
            />
            <CardContent>
              <div className="space-y-3">
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Author:</span> Chintan Tank
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Status:</span> Presented via recorded submission in the official ICCCNet 2026 online program
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Springer Lecture Notes in Networks and Systems publication is pending. No DOI has been assigned.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={FileText}
              title="Design and Evaluation of SLM-Serv: A Lightweight 1B Parameter Architecture for Privacy-Preserving Local Adaptation"
              subtitle="Springer Conference Paper (2026) - Presented"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
            />
            <CardContent>
              <div className="space-y-3">
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Authors:</span> Kolla, V.G., Kasimala, K., Tank, C.D.
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Presented at:</span> 7th International Conference on Data Analytics &amp; Management (ICDAM-2026)
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Conference date:</span> June 12–14, 2026, London, UK
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Publisher:</span> Springer Lecture Notes in Networks and Systems (LNNS)
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Indexing:</span> Scopus, EI Compendex, INSPEC, Web of Science
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  <span className="font-medium">Status:</span> Presented. Springer LNNS publication is pending; no DOI has been assigned.
                </p>
                <p className={getThemeClass('colors.text.secondary')}>
                  Introduces SLM-Serv, a 1B-parameter architecture for privacy-preserving local model adaptation via federated learning. Demonstrates 17.4x reduction in communication overhead and 22.8x improvement in adaptation efficiency over federated baselines.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Co-Authored Research Publications - NEW SECTION */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Co-Authored Research Publications"
          subtitle="Contributing to interdisciplinary research infrastructure and academic knowledge"
          align="left"
        />
        
        <div className="grid grid-cols-1 gap-6 mb-10">
          {coAuthoredPublications.map((pub, idx) => (
            <Card 
              key={idx}
              variant="bordered"
              className={cn(
                'transition-all duration-300',
                getThemeClass('colors.border.hover', 'borderHover')
              )}
            >
              <CardHeader
                icon={FileText}
                title={pub.title}
                subtitle={'authors' in pub ? pub.authors : `Contribution: ${pub.contribution}`}
                iconClassName={cn(
                  'w-6 h-6',
                  getThemeClass('colors.primary.main')
                )}
              />
              <CardContent>
                <div className="space-y-3">
                  {'conference' in pub && (
                    <p className={getThemeClass('colors.text.secondary')}>
                      <span className="font-medium">Conference:</span> {pub.conference} | Pages: {pub.pages}
                    </p>
                  )}
                  {'collaboration' in pub && (
                    <p className={getThemeClass('colors.text.secondary')}>
                      <span className="font-medium">Collaboration:</span> {pub.collaboration}
                    </p>
                  )}
                  <p className={getThemeClass('colors.text.secondary')}>
                    {pub.description}
                  </p>
                  {'sustainedImpact' in pub && (
                    <p className={cn('italic', getThemeClass('colors.text.secondary'))}>
                      <span className="font-medium">Sustained Impact:</span> {pub.sustainedImpact}
                    </p>
                  )}
                  {'tools' in pub && pub.tools && (
                    <TagList
                      tags={pub.tools.map(tool => ({
                        label: tool,
                        variant: 'outline',
                        size: 'sm'
                      }))}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Professional Recognition & Credentials - NEW SECTION */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Professional Recognition & Credentials"
          subtitle="Industry recognition and active contribution to technical peer review"
          align="left"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Professional Memberships */}
          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={GraduationCap}
              title="Professional Memberships"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
            />
            <CardContent>
              <div className="space-y-4">
                {professionalRecognition.memberships.map((membership, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      'border-l-4 pl-4',
                      getThemeClass('colors.primary.main', 'borderHover')
                    )}
                  >
                    <h4 className={cn(
                      'font-semibold',
                      getThemeClass('colors.text.primary')
                    )}>
                      {membership.title}
                    </h4>
                    <p className={cn(
                      'text-sm',
                      getThemeClass('colors.text.secondary')
                    )}>
                      {membership.organization}
                    </p>
                    <p className={cn(
                      'text-sm mt-1',
                      getThemeClass('colors.text.secondary')
                    )}>
                      {membership.description}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Current Professional Activities */}
          <Card
            variant="bordered"
            className={cn(
              'transition-all duration-300',
              getThemeClass('colors.border.hover', 'borderHover')
            )}
          >
            <CardHeader
              icon={Users}
              title="Current Professional Activities"
              iconClassName={cn(
                'w-6 h-6',
                getThemeClass('colors.primary.main')
              )}
            />
            <CardContent>
              <ul className="space-y-3">
                {professionalRecognition.activities.map((activity, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className={cn(
                      'w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0',
                      'bg-sky-500'
                    )}/>
                    <span className={getThemeClass('colors.text.secondary')}>
                      {activity}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Patents Section - Expanded with Impact Metrics */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Patents & Innovation"
          subtitle="Recognized contributions to technical innovation through patent grants with documented industry impact"
          align="left"
        />
        
        <div className="grid grid-cols-1 gap-6 mb-10">
          {patents.map((patent, idx) => (
            <Card 
              key={idx} 
              variant="bordered"
              className={cn(
                'transition-all duration-300',
                getThemeClass('colors.border.hover', 'borderHover')
              )}
            >
              <CardHeader
                icon={Award}
                title={patent.title}
                subtitle={`${patent.id} · Issued ${patent.issued}`}
                iconClassName={cn(
                  'w-6 h-6',
                  getThemeClass('colors.primary.main')
                )}
                link={patent.link}
              />
              <CardContent>
                <div className="space-y-4">
                  <p className={getThemeClass('colors.text.secondary')}>
                    {patent.description}
                  </p>
                  
                  {/* Innovation Impact */}
                  <div className={cn(
                    'rounded-lg p-4',
                    'bg-gradient-to-r from-amber-50 to-orange-50',
                    'border border-amber-200'
                  )}>
                    <p className={cn('font-semibold mb-3', getThemeClass('colors.text.primary'))}>
                      Innovation Impact
                    </p>
                    <ul className="space-y-2">
                      {patent.impact.map((item, impactIdx) => (
                        <li key={impactIdx} className="flex items-start">
                          <span className="w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0 bg-amber-500"/>
                          <span className={cn('text-sm', getThemeClass('colors.text.secondary'))}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>
      
      {/* Technical Workshops Section */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Technical Workshops & Presentations"
          subtitle="Leading knowledge sharing initiatives and technical discussions across academic institutions"
          align="left"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          {workshops.map((workshop, idx) => (
            <Card 
              key={idx}
              className={cn(
                'transition-all duration-300',
                getThemeClass('colors.border.hover', 'borderHover')
              )}
            >
              <CardHeader
                icon={Users}
                title={workshop.title}
                iconClassName={cn(
                  'w-6 h-6',
                  getThemeClass('colors.primary.main')
                )}
              />
              <CardContent>
                <div className="space-y-4">
                  {isConferenceWorkshop(workshop) ? (
                    // Conference workshops
                    workshop.events.map((event, eventIdx) => (
                      <div 
                        key={eventIdx} 
                        className={cn(
                          'border-l-4 pl-4',
                          getThemeClass('colors.primary.main', 'borderHover')
                        )}
                      >
                        <h4 className={cn(
                          'font-semibold',
                          getThemeClass('colors.text.primary')
                        )}>
                          {event.name}
                        </h4>
                        <p className={cn(
                          'text-sm mb-2',
                          getThemeClass('colors.text.secondary')
                        )}>
                          {event.location}
                        </p>
                        <ul className="space-y-1">
                          {event.topics.map((topic, topicIdx) => (
                            <li 
                              key={topicIdx} 
                              className={cn(
                                'text-sm flex items-start',
                                getThemeClass('colors.text.secondary')
                              )}
                            >
                              <span className={cn(
                                'w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0',
                                getThemeClass('colors.primary.surface')
                              )}/>
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  ) : (
                    // Institution workshops
                    <>
                      <p className={cn(
                        'font-medium',
                        getThemeClass('colors.text.primary')
                      )}>
                        {workshop.institution}
                      </p>
                      <div className="space-y-2">
                        {workshop.contributions.map((contribution, contribIdx) => (
                          <p 
                            key={contribIdx}
                            className={cn(
                              'flex items-start',
                              getThemeClass('colors.text.secondary')
                            )}
                          >
                            <span className={cn(
                              'w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0',
                              getThemeClass('colors.primary.surface')
                            )}/>
                            {contribution}
                          </p>
                        ))}
                      </div>
                      <div className="mt-4">
                        <TagList
                          tags={workshop.tools.map(tool => ({
                            label: tool,
                            variant: 'outline',
                            size: 'sm'
                          }))}
                        />
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Technical Contributions Section */}
      <Section background="light" spacing="sm">
        <SectionHeader
          title="Technical Contributions"
          subtitle="Developing foundational tools and frameworks for academic research"
          align="left"
        />
        
        <Card className={cn(
          'transition-all duration-300',
          getThemeClass('colors.border.hover', 'borderHover')
        )}>
          <CardHeader
            icon={LightbulbIcon}
            title="Framework Development"
            iconClassName={cn(
              'w-6 h-6',
              getThemeClass('colors.primary.main')
            )}
          />
          <CardContent>
            <div className="space-y-6">
              <div>
                <h4 className={cn(
                  'text-lg font-semibold mb-2',
                  getThemeClass('colors.text.primary')
                )}>
                  Core Framework Development at CNS Lab
                </h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className={cn(
                      'w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0',
                      getThemeClass('colors.primary.surface')
                    )}/>
                    <span className={getThemeClass('colors.text.secondary')}>
                      Developed foundational visualization plugins
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className={cn(
                      'w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0',
                      getThemeClass('colors.primary.surface')
                    )}/>
                    <span className={getThemeClass('colors.text.secondary')}>
                      Implemented cross-disciplinary data analysis tools
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className={cn(
                      'w-2 h-2 rounded-full mt-2 mr-2 flex-shrink-0',
                      getThemeClass('colors.primary.surface')
                    )}/>
                    <span className={getThemeClass('colors.text.secondary')}>
                      Created network analysis frameworks
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className={cn(
                  'text-lg font-semibold mb-2',
                  getThemeClass('colors.text.primary')
                )}>
                  Project Contributions
                </h4>
                <TagList
                  tags={[
                    { label: 'Network Workbench (NWB)', variant: 'primary' },
                    { label: 'Science of Science (Sci2)', variant: 'primary' },
                    { label: 'Epidemics Cyberinfrastructure (EpiC)', variant: 'primary' },
                    { label: 'VIVO Network', variant: 'primary' }
                  ]}
                  spacing="md"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>
    </PageLayout>
  );
};

export default Research;
